import { hasPromotionalOffersAccess } from './feature-access';

export type PlanId = 'basic' | 'starter' | 'growth' | 'enterprise';

export interface PlanFeature {
  id: string;
  name: string;
  description: string;
  included: boolean;
}

export interface PlanUsagePricing {
  includedPlays: number;
  overageRate: number;
  cappedAmountUSD: number;
  terms: string;
}

export interface Plan {
  id: PlanId;
  name: string;
  price: number;
  billingCycle: 'monthly' | 'yearly';
  trialDays: number;
  features: PlanFeature[];
  limits: {
    activeGames: number;
    monthlyPlays: number;
  };
  popular?: boolean;
  usagePricing?: PlanUsagePricing;
}

export const PLANS_CONFIG: Plan[] = [
  {
    id: 'basic',
    name: 'Basic',
    price: 0,
    billingCycle: 'monthly',
    trialDays: 0,
    limits: {
      activeGames: 1,
      monthlyPlays: 25,
    },
    features: [
      // Included features first (✓)
      {
        id: 'active-games',
        name: '1 Active Games',
        description: '1 active game only',
        included: true,
      },
      {
        id: 'monthly-plays',
        name: '25 Free Plays',
        description: 'First 25 plays free (lifetime), then $0.15/play',
        included: true,
      },
      {
        id: 'localization',
        name: 'Localization',
        description: 'Multi-language support',
        included: true,
      },
      // Excluded features after (◯)
      {
        id: 'customer-emails',
        name: 'Customer Emails with Custom Timing',
        description: 'Custom email timing and scheduling',
        included: false,
      },
      {
        id: 'promotional-offers',
        name: 'Promotional Offers on Selected Products',
        description: 'Product-specific promotional offers',
        included: false,
      },
      {
        id: 'priority-support',
        name: 'Priority / Personal Support',
        description: 'Priority support and personal assistance',
        included: false,
      },
      {
        id: 'ai-features',
        name: 'AI Features',
        description: 'AI-powered features and personalization',
        included: false,
      },
      {
        id: 'premium-integrations',
        name: 'Premium Integrations',
        description: 'Advanced third-party integrations',
        included: false,
      },
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 14.99,
    billingCycle: 'monthly',
    popular: true,
    trialDays: 14,
    limits: {
      activeGames: 2,
      monthlyPlays: 1000,
    },
    usagePricing: {
      includedPlays: 1000,
      overageRate: 0.15,
      cappedAmountUSD: 100,
      terms: 'Includes up to 1,000 plays per month. Additional plays billed at $0.15 each (capped at $500 per billing cycle).',
    },
    features: [
      // Included features first (✓)
      {
        id: 'active-games',
        name: '2 Active Games',
        description: '2 active games',
        included: true,
      },
      {
        id: 'monthly-plays',
        name: 'Up to 1,000 plays',
        description: 'Includes up to 1,000 plays each month, additional plays billed at $0.15',
        included: true,
      },
      {
        id: 'customer-emails',
        name: 'Customer Emails with Custom Timing',
        description: 'Custom email timing and scheduling',
        included: true,
      },
      {
        id: 'promotional-offers',
        name: 'Promotional Offers on Selected Products',
        description: 'Product-specific promotional offers',
        included: true,
      },
      {
        id: 'localization',
        name: 'Localization (1 additional language)',
        description: '1 additional language',
        included: true,
      },
      // Excluded features after (◯)
      {
        id: 'priority-support',
        name: 'Priority / Personal Support',
        description: 'Priority support and personal assistance',
        included: false,
      },
      {
        id: 'ai-features',
        name: 'AI Features',
        description: 'AI-powered features and personalization',
        included: false,
      },
      {
        id: 'premium-integrations',
        name: 'Premium Integrations',
        description: 'Advanced third-party integrations',
        included: false,
      },
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 34.99,
    billingCycle: 'monthly',
    trialDays: 14,
    limits: {
      activeGames: 4,
      monthlyPlays: 3000,
    },
    usagePricing: {
      includedPlays: 3000,
      overageRate: 0.12,
      cappedAmountUSD: 100,
      terms: 'Includes up to 3,000 plays per month. Additional plays billed at $0.12 each (capped at $1,500 per billing cycle).',
    },
    features: [
      // Included features first (✓)
      {
        id: 'active-games',
        name: '4 Active Games',
        description: '4 active games',
        included: true,
      },
      {
        id: 'monthly-plays',
        name: 'Up to 3,000 plays',
        description: 'Includes up to 3,000 plays each month, additional plays billed at $0.12',
        included: true,
      },
      {
        id: 'customer-emails',
        name: 'Customer Emails with Custom Timing',
        description: 'Custom email timing and scheduling',
        included: true,
      },
      {
        id: 'promotional-offers',
        name: 'Promotional Offers on Selected Products',
        description: 'Product-specific promotional offers',
        included: true,
      },
      {
        id: 'priority-support',
        name: 'Priority / Personal Support',
        description: 'Priority support and personal assistance',
        included: true,
      },
      {
        id: 'localization',
        name: 'Localization (up to 4 additional languages)',
        description: 'Multiple languages',
        included: true,
      },
      {
        id: 'ai-features',
        name: 'AI Features',
        description: 'AI-powered features and personalization',
        included: true,
      },
      // Excluded features after (◯)
      {
        id: 'premium-integrations',
        name: 'Premium Integrations',
        description: 'Advanced third-party integrations',
        included: false,
      },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 99.99,
    billingCycle: 'monthly',
    trialDays: 14,
    limits: {
      activeGames: -1, // -1 indicates unlimited
      monthlyPlays: 50000,
    },
    usagePricing: {
      includedPlays: 50000,
      overageRate: 0.10,
      cappedAmountUSD: 100,
      terms: 'Includes up to 50,000 plays per month. Additional plays billed at $0.10 each (capped at $5,000 per billing cycle).',
    },
    features: [
      // All features included (✓)
      {
        id: 'active-games',
        name: 'Unlimited Active Games',
        description: 'No limit on active games',
        included: true,
      },
      {
        id: 'monthly-plays',
        name: 'Up to 50,000 plays',
        description: 'Includes up to 50,000 plays each month, additional plays billed at $0.10',
        included: true,
      },
      {
        id: 'customer-emails',
        name: 'Customer Emails with Custom Timing',
        description: 'Custom email timing and scheduling',
        included: true,
      },
      {
        id: 'promotional-offers',
        name: 'Promotional Offers on Selected Products',
        description: 'Product-specific promotional offers',
        included: true,
      },
      {
        id: 'priority-support',
        name: 'Priority / Personal Support',
        description: 'Priority support and personal assistance',
        included: true,
      },
      {
        id: 'localization',
        name: 'Localization',
        description: 'Multiple languages',
        included: true,
      },
      {
        id: 'ai-features',
        name: 'AI Features',
        description: 'AI-powered features and personalization',
        included: true,
      },
      {
        id: 'premium-integrations',
        name: 'Premium Integrations',
        description: 'Advanced third-party integrations',
        included: true,
      },
    ],
  },
];

interface PlanSelectionOptions {
  includeBasic?: boolean;
}

export const getSelectablePlans = (
  options: PlanSelectionOptions = {}
): Plan[] => {
  const { includeBasic = true } = options;
  return includeBasic ? [...PLANS_CONFIG] : PLANS_CONFIG.filter(plan => plan.id !== 'basic');
};

export const getPlanById = (planId: PlanId): Plan | undefined => {
  return PLANS_CONFIG.find(plan => plan.id === planId);
};

export const getPlanFeatures = (planId: PlanId): PlanFeature[] => {
  const plan = getPlanById(planId);
  return plan?.features || [];
};

export const isFeatureIncluded = (planId: PlanId, featureId: string, userCreatedAt?: Date | string | any): boolean => {
  const plan = getPlanById(planId);
  const planFeature = plan?.features.find(feature => feature.id === featureId);
  
  // If feature is not found in plan, return false
  if (!planFeature) {
    return false;
  }
  
  // If feature is included in plan, return true
  if (planFeature.included) {
    return true;
  }
  
  // If feature is not included in plan, check if user has date-based access
  if (userCreatedAt && featureId === 'promotional-offers') {
    const hasDateAccess = hasPromotionalOffersAccess(userCreatedAt);
    return hasDateAccess;
  }
  
  return false;
};

export const getPlanLimits = (planId: PlanId) => {
  const plan = getPlanById(planId);
  return plan?.limits || { activeGames: 0, monthlyPlays: 0 };
};

export const getPlanTrial = (planId: PlanId) => {
  const plan = getPlanById(planId);
  return plan?.trialDays || 0;
};
